import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { Star, Send, CheckCircle, Heart } from 'lucide-react';
import axios from 'axios';

const API = '/api';

const RatingSelector = ({ value, onChange, max = 5, min = 1 }) => {
  const options = [];
  for (let i = min; i <= max; i++) {
    options.push(i);
  }

  return (
    <div className="flex items-center gap-1">
      {options.map(n => (
        <button
          key={n}
          onClick={() => onChange(n)}
          className={`w-10 h-10 rounded-lg border-2 font-medium transition-all ${
            value === n
              ? 'bg-blue-500 border-blue-500 text-white scale-110'
              : 'border-slate-200 hover:border-blue-300 hover:bg-blue-50'
          }`}
        >
          {n}
        </button>
      ))}
    </div>
  );
};

const NPSSelector = ({ value, onChange }) => {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1">
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
          <button
            key={n}
            onClick={() => onChange(n)}
            className={`w-9 h-9 rounded-lg border-2 text-sm font-medium transition-all ${
              value === n
                ? n <= 6
                  ? 'bg-red-500 border-red-500 text-white'
                  : n <= 8
                  ? 'bg-amber-500 border-amber-500 text-white'
                  : 'bg-green-500 border-green-500 text-white'
                : 'border-slate-200 hover:border-blue-300'
            }`}
          >
            {n}
          </button>
        ))}
      </div>
      <div className="flex justify-between text-xs text-muted-foreground px-1">
        <span>Deloc probabil</span>
        <span>Foarte probabil</span>
      </div>
    </div>
  );
};

export const ClientSurvey = () => {
  const { token } = useParams();
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [answers, setAnswers] = useState({});
  const [clientInfo, setClientInfo] = useState({
    client_name: '',
    client_email: '',
    positive_feedback: '',
    improvement_suggestions: ''
  });

  useEffect(() => {
    loadSurvey();
  }, [token]);

  const loadSurvey = async () => {
    try {
      const res = await axios.get(`${API}/survey/${token}`);
      if (res.data.status === 'completed') {
        setSubmitted(true);
      } else {
        setSurvey(res.data);
      }
    } catch (err) {
      toast.error('Chestionarul nu a fost găsit');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    // Validate all questions answered
    const requiredQuestions = survey.questions.map(q => q.id);
    const missing = requiredQuestions.filter(id => answers[id] === undefined);
    
    if (missing.length > 0) {
      toast.error('Te rugăm să răspunzi la toate întrebările');
      return;
    }

    setSubmitting(true);
    try {
      const answerList = Object.entries(answers).map(([question_id, rating]) => ({
        question_id,
        rating
      }));

      await axios.post(`${API}/survey/${token}/submit`, {
        answers: answerList,
        ...clientInfo
      });

      setSubmitted(true);
      toast.success('Mulțumim pentru feedback!');
    } catch (err) {
      toast.error('Eroare la trimitere. Te rugăm să încerci din nou.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
        <Toaster position="top-center" />
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-green-700 mb-2">
              Mulțumim!
            </h2>
            <p className="text-muted-foreground">
              Feedback-ul tău a fost înregistrat cu succes. Apreciem timpul acordat!
            </p>
            <div className="mt-6 flex items-center justify-center gap-1 text-pink-500">
              <Heart className="w-5 h-5 fill-current" />
              <span className="text-sm">Echipa Adsem</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!survey) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-8 pb-8">
            <p className="text-muted-foreground">
              Chestionarul nu a fost găsit sau a expirat.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Group questions by category
  const generalQuestions = survey.questions.filter(q => q.category === 'general');
  const teamQuestions = survey.questions.filter(q => q.category === 'team');
  const npsQuestion = survey.questions.find(q => q.is_nps);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <Toaster position="top-center" />
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <Card>
          <CardHeader className="text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Star className="w-6 h-6 text-blue-600" />
            </div>
            <CardTitle className="text-xl">
              Chestionar Satisfacție Client
            </CardTitle>
            <CardDescription>
              Proiect: <strong>{survey.project_name}</strong>
            </CardDescription>
            <p className="text-sm text-muted-foreground mt-2">
              Ajută-ne să îmbunătățim serviciile noastre răspunzând la câteva întrebări.
              Durează mai puțin de 2 minute.
            </p>
          </CardHeader>
        </Card>

        {/* General Satisfaction */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Satisfacție Generală</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {generalQuestions.map(q => (
              <div key={q.id}>
                <Label className="mb-2 block">{q.question_ro}</Label>
                <div className="flex items-center gap-4">
                  <RatingSelector
                    value={answers[q.id]}
                    onChange={(v) => setAnswers(a => ({ ...a, [q.id]: v }))}
                    min={q.scale_min}
                    max={q.scale_max}
                  />
                  <div className="text-sm text-muted-foreground">
                    <span>1 = Deloc mulțumit</span>
                    <span className="mx-2">•</span>
                    <span>5 = Foarte mulțumit</span>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Team Relationship */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Relația cu Echipa</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {teamQuestions.map(q => (
              <div key={q.id}>
                <Label className="mb-2 block">{q.question_ro}</Label>
                <RatingSelector
                  value={answers[q.id]}
                  onChange={(v) => setAnswers(a => ({ ...a, [q.id]: v }))}
                  min={q.scale_min}
                  max={q.scale_max}
                />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* NPS */}
        {npsQuestion && (
          <Card className="border-2 border-blue-200">
            <CardHeader>
              <CardTitle className="text-lg">Recomandare</CardTitle>
            </CardHeader>
            <CardContent>
              <Label className="mb-3 block">{npsQuestion.question_ro}</Label>
              <NPSSelector
                value={answers[npsQuestion.id]}
                onChange={(v) => setAnswers(a => ({ ...a, [npsQuestion.id]: v }))}
              />
            </CardContent>
          </Card>
        )}

        {/* Additional Feedback */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Feedback Adițional</CardTitle>
            <CardDescription>Opțional, dar foarte apreciat</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Ce apreciezi cel mai mult la colaborarea cu noi?</Label>
              <Textarea
                value={clientInfo.positive_feedback}
                onChange={(e) => setClientInfo(c => ({ ...c, positive_feedback: e.target.value }))}
                placeholder="Spune-ne ce funcționează bine..."
                rows={3}
                className="mt-2"
              />
            </div>
            <div>
              <Label>Ce am putea îmbunătăți?</Label>
              <Textarea
                value={clientInfo.improvement_suggestions}
                onChange={(e) => setClientInfo(c => ({ ...c, improvement_suggestions: e.target.value }))}
                placeholder="Sugestiile tale ne ajută să creștem..."
                rows={3}
                className="mt-2"
              />
            </div>
          </CardContent>
        </Card>

        {/* Contact Info (optional) */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Informații Contact</CardTitle>
            <CardDescription>Opțional - pentru follow-up</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4">
            <div>
              <Label>Nume</Label>
              <Input
                value={clientInfo.client_name}
                onChange={(e) => setClientInfo(c => ({ ...c, client_name: e.target.value }))}
                placeholder="Numele tău"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={clientInfo.client_email}
                onChange={(e) => setClientInfo(c => ({ ...c, client_email: e.target.value }))}
                placeholder="email@exemplu.com"
                className="mt-1"
              />
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <Button
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full h-12 text-lg bg-blue-600 hover:bg-blue-700"
        >
          {submitting ? (
            <span className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
              Se trimite...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Send className="w-5 h-5" />
              Trimite Răspunsurile
            </span>
          )}
        </Button>

        <p className="text-center text-sm text-muted-foreground">
          Răspunsurile tale sunt confidențiale și ne ajută să îmbunătățim serviciile.
        </p>
      </div>
    </div>
  );
};

export default ClientSurvey;
